/**
 * Chord note derivation: chord model → the notes it contains.
 */
import { getEnharmonicallyCorrectNoteName, getNoteIndex, getBaseNote, getExpectedLetterForIntervalRole, normalizeUncommonSpelling } from './enharmonicUtils.js';
import { normalizeToV3 } from './chordModelMigration.js';
import { formatNote } from './noteFormatting.js';
/**
 * Get the notes in a chord from a chord model.
 *
 * Accepts V1, V2, and V3 chord models.
 * Internally converts to V3 for processing.
 *
 * @param chord - Chord model in any format
 * @returns Array of note names in the chord
 */
export const getChordNotes = (chord) => {
    // Normalize to V3 for internal processing
    const v3 = normalizeToV3(chord);
    // Handle null root
    if (!v3.root) {
        return [];
    }
    // Normalize uncommon spellings (B♯→C, E♯→F, C♭→B, F♭→E) so enharmonic
    // equivalents produce identical chord notes. The letter-based interval
    // spelling below otherwise diverges for these roots (KNOWN_BUGS.md Bug 10).
    const fundamental = normalizeUncommonSpelling(formatNote(v3.root));
    const fundamentalIndex = getNoteIndex(fundamental);
    if (fundamentalIndex === -1) {
        return [];
    }
    const notesInChord = [fundamental];
    // Use composite quality to determine which notes to include
    const quality = getCompositeQualityString(v3);
    const adds = v3.adds;
    const alterations = v3.alterations;
    // Bass gets the same uncommon-spelling normalization as the root so a bass
    // equal to the root (e.g. C♭/C♭) is recognized as such after normalization.
    const bass = v3.bass ? normalizeUncommonSpelling(formatNote(v3.bass)) : null;
    let noteIndexGlobal; // Renamed to avoid conflict with block-scoped noteIndex
    const addNoteToChord = (semitoneOffset, intervalRole, insertAt) => {
        const potentialNotePitchIndex = (fundamentalIndex + semitoneOffset) % 12;
        const currentPitchIndexes = new Set(notesInChord.map(n => getNoteIndex(n)).filter(idx => idx !== -1));
        let shouldAdd = true;
        if (currentPitchIndexes.has(potentialNotePitchIndex)) {
            // Allow adding if it's the fundamental itself for roles other than 1 (e.g. octave)
            // AND if the fundamental is the only note currently (or if the note isn't the fundamental)
            if (potentialNotePitchIndex === fundamentalIndex && intervalRole !== 1 && notesInChord.length === 1) {
                // This case is okay (e.g. adding an octave as the second note)
            }
            else if (potentialNotePitchIndex === fundamentalIndex && intervalRole === 1) {
                // Trying to re-add the fundamental with role 1 - skip
                shouldAdd = false;
            }
            else if (potentialNotePitchIndex !== fundamentalIndex) {
                // It's a duplicate of a non-fundamental note
                shouldAdd = false;
            }
            else if (notesInChord.length > 1 && potentialNotePitchIndex === fundamentalIndex && intervalRole === 1) {
                // Trying to re-add fundamental with role 1 when other notes exist
                shouldAdd = false;
            }
        }
        if (shouldAdd) {
            noteIndexGlobal = (fundamentalIndex + semitoneOffset) % 12;
            const rootBaseLetter = getBaseNote(fundamental);
            const targetLetter = getExpectedLetterForIntervalRole(rootBaseLetter, intervalRole);
            const newNoteName = getEnharmonicallyCorrectNoteName(noteIndexGlobal, fundamental, quality, notesInChord, targetLetter);
            // Final check for exact name string if pitch was somehow different (highly unlikely with current logic)
            // or if it's genuinely a new note name for an existing pitch (e.g. C and B# - though our system avoids this)
            if (!notesInChord.includes(newNoteName)) {
                if (insertAt !== undefined && insertAt >= 0 && insertAt <= notesInChord.length) {
                    notesInChord.splice(insertAt, 0, newNoteName);
                }
                else {
                    notesInChord.push(newNoteName);
                }
            }
            else {
                // If name is identical, we assume it's the same note, already covered by pitch check.
                // This can be refined if roles need to make same-named notes distinct (e.g. theoretical double-stops)
            }
        }
    };
    switch (quality) {
        case 'major':
        case '':
        case null:
            addNoteToChord(4, 3);
            addNoteToChord(7, 5);
            break;
        case 'minor':
            addNoteToChord(3, 3);
            addNoteToChord(7, 5);
            break;
        case 'diminished':
            addNoteToChord(3, 3);
            addNoteToChord(6, 5);
            break;
        case 'augmented':
            addNoteToChord(4, 3);
            addNoteToChord(8, 5);
            break;
        case 'sus2':
            addNoteToChord(2, 2);
            addNoteToChord(7, 5);
            break;
        case 'sus4':
            addNoteToChord(5, 4);
            addNoteToChord(7, 5);
            break;
        case 'power':
            addNoteToChord(7, 5);
            break;
        case 'major7':
            addNoteToChord(4, 3);
            addNoteToChord(7, 5);
            addNoteToChord(11, 7);
            break;
        case 'major6':
            addNoteToChord(4, 3);
            addNoteToChord(7, 5);
            addNoteToChord(9, 6);
            break;
        case 'major9':
            addNoteToChord(4, 3);
            addNoteToChord(7, 5);
            addNoteToChord(11, 7);
            addNoteToChord(14, 9);
            break;
        case 'major11':
            addNoteToChord(7, 5);
            addNoteToChord(11, 7);
            addNoteToChord(14, 9);
            addNoteToChord(17, 11);
            break;
        case 'major13':
            addNoteToChord(4, 3);
            addNoteToChord(11, 7);
            addNoteToChord(14, 9);
            addNoteToChord(21, 13);
            break;
        case 'minor7':
            addNoteToChord(3, 3);
            addNoteToChord(7, 5);
            addNoteToChord(10, 7);
            break;
        case 'minor6':
            addNoteToChord(3, 3);
            addNoteToChord(7, 5);
            addNoteToChord(9, 6);
            break;
        case 'minor9':
            addNoteToChord(3, 3);
            addNoteToChord(7, 5);
            addNoteToChord(10, 7);
            addNoteToChord(14, 9);
            break;
        case 'minor11':
            addNoteToChord(3, 3);
            addNoteToChord(10, 7);
            addNoteToChord(14, 9);
            addNoteToChord(17, 11);
            break;
        case 'minor13':
            addNoteToChord(3, 3);
            addNoteToChord(10, 7);
            addNoteToChord(14, 9);
            addNoteToChord(21, 13);
            break;
        case 'diminished7':
            addNoteToChord(3, 3);
            addNoteToChord(6, 5);
            addNoteToChord(9, 7);
            break;
        case 'diminished6':
            addNoteToChord(3, 3);
            addNoteToChord(6, 5);
            addNoteToChord(9, 6);
            break;
        case 'diminished9':
            addNoteToChord(3, 3);
            addNoteToChord(6, 5);
            addNoteToChord(9, 7);
            addNoteToChord(13, 9);
            break;
        case 'augmented7':
            addNoteToChord(4, 3);
            addNoteToChord(8, 5);
            addNoteToChord(10, 7);
            break;
        case 'augmented9':
            addNoteToChord(4, 3);
            addNoteToChord(8, 5);
            addNoteToChord(10, 7);
            addNoteToChord(14, 9);
            break;
        case 'dominant7':
            addNoteToChord(4, 3);
            addNoteToChord(7, 5);
            addNoteToChord(10, 7);
            break;
        case 'dominant9':
            addNoteToChord(4, 3);
            addNoteToChord(7, 5);
            addNoteToChord(10, 7);
            addNoteToChord(14, 9);
            break;
        case 'dominant11':
            addNoteToChord(7, 5);
            addNoteToChord(10, 7);
            addNoteToChord(14, 9);
            addNoteToChord(17, 11);
            break;
        case 'dominant13':
            addNoteToChord(4, 3);
            addNoteToChord(7, 5);
            addNoteToChord(10, 7);
            addNoteToChord(14, 9);
            addNoteToChord(21, 13);
            break;
        case 'dominant7sus2':
            addNoteToChord(2, 2);
            addNoteToChord(7, 5);
            addNoteToChord(10, 7);
            break;
        case 'dominant7sus4':
            addNoteToChord(5, 4);
            addNoteToChord(7, 5);
            addNoteToChord(10, 7);
            break;
        case 'dominant9sus4':
            addNoteToChord(5, 4);
            addNoteToChord(7, 5);
            addNoteToChord(10, 7);
            addNoteToChord(14, 9);
            break;
        // Minor Major Chords
        case 'minorMajor7':
            addNoteToChord(3, 3); // Minor 3rd
            addNoteToChord(7, 5); // Perfect 5th
            addNoteToChord(11, 7); // Major 7th
            break;
        case 'minorMajor9':
            addNoteToChord(3, 3); // Minor 3rd
            addNoteToChord(7, 5); // Perfect 5th
            addNoteToChord(11, 7); // Major 7th
            addNoteToChord(14, 9); // Major 9th
            break;
        case 'minorMajor11':
            addNoteToChord(3, 3); // Minor 3rd
            addNoteToChord(7, 5); // Perfect 5th
            addNoteToChord(11, 7); // Major 7th
            addNoteToChord(14, 9); // Major 9th
            addNoteToChord(17, 11); // Perfect 11th
            break;
        case 'minorMajor13':
            addNoteToChord(3, 3); // Minor 3rd
            addNoteToChord(7, 5); // Perfect 5th
            addNoteToChord(11, 7); // Major 7th
            addNoteToChord(14, 9); // Major 9th
            addNoteToChord(17, 11); // Perfect 11th
            addNoteToChord(21, 13); // Major 13th
            break;
        default: break;
    }
    if (adds && adds.length > 0 && fundamentalIndex !== -1) {
        const addIntervals = {
            add2: { offset: 2, role: 2 }, add4: { offset: 5, role: 4 },
            add6: { offset: 9, role: 6 }, add9: { offset: 14, role: 9 },
            add11: { offset: 17, role: 11 }, add13: { offset: 21, role: 13 },
        };
        const currentNotePitchIndexesForAdds = new Set(notesInChord.map(noteName => getNoteIndex(noteName)).filter(idx => idx !== -1));
        for (const add of adds) {
            if (add === "-")
                continue;
            const intervalInfo = addIntervals[add];
            if (intervalInfo) {
                const potentialAddNotePitchIndex = (fundamentalIndex + intervalInfo.offset) % 12;
                if (!currentNotePitchIndexesForAdds.has(potentialAddNotePitchIndex)) {
                    addNoteToChord(intervalInfo.offset, intervalInfo.role);
                    currentNotePitchIndexesForAdds.add(potentialAddNotePitchIndex);
                }
            }
        }
    }
    if (alterations && alterations.length > 0 && fundamentalIndex !== -1) {
        const alterationMap = {
            "♭5": { role: 5, offset: 6 }, "♯5": { role: 5, offset: 8 },
            "♭9": { role: 9, offset: 13 }, "♯9": { role: 9, offset: 15 },
            "♯11": { role: 11, offset: 18 }, "♭13": { role: 13, offset: 20 },
        };
        const alterationsToApply = [...alterations].sort((a, b) => (alterationMap[a]?.role || 0) - (alterationMap[b]?.role || 0));
        for (const alt of alterationsToApply) {
            if (alt === "-")
                continue;
            const altInfo = alterationMap[alt];
            if (!altInfo)
                continue;
            const rootBase = getBaseNote(fundamental);
            const expectedLetterOfTarget = getExpectedLetterForIntervalRole(rootBase, altInfo.role);
            let indexOfNoteToReplace = -1;
            for (let i = 0; i < notesInChord.length; i++) {
                if (getBaseNote(notesInChord[i]) === expectedLetterOfTarget) {
                    indexOfNoteToReplace = i;
                    break;
                }
            }
            const potentialAlteredNotePitchIndex = (fundamentalIndex + altInfo.offset) % 12;
            const currentNotePitchIndexes = new Set(notesInChord.map(n => getNoteIndex(n)).filter(idx => idx !== -1));
            if (indexOfNoteToReplace !== -1) { // A note of the target role's letter name exists
                const noteBeingReplacedPitchIndex = getNoteIndex(notesInChord[indexOfNoteToReplace]);
                // Only replace if the new altered pitch is different from the one being replaced,
                // AND this new pitch isn't a duplicate of some *other* existing note.
                if (potentialAlteredNotePitchIndex !== noteBeingReplacedPitchIndex && !currentNotePitchIndexes.has(potentialAlteredNotePitchIndex)) {
                    notesInChord.splice(indexOfNoteToReplace, 1);
                    // Insert at the replaced note's position so interval order is preserved
                    // (C7♭5 → C E G♭ B♭, not C E B♭ G♭) — consumers derive voicings from it
                    addNoteToChord(altInfo.offset, altInfo.role, indexOfNoteToReplace);
                }
                else if (potentialAlteredNotePitchIndex === noteBeingReplacedPitchIndex) {
                    // If same pitch, but spelling might change (e.g. G to G for b5 on Caug)
                    notesInChord.splice(indexOfNoteToReplace, 1);
                    addNoteToChord(altInfo.offset, altInfo.role, indexOfNoteToReplace);
                }
                // If it would create a duplicate of *another* note, we do nothing here (don't replace, don't add).
            }
            else { // No note of the target role's letter name exists, so we add the altered note if pitch is unique.
                if (!currentNotePitchIndexes.has(potentialAlteredNotePitchIndex)) {
                    addNoteToChord(altInfo.offset, altInfo.role);
                }
            }
        }
    }
    // Handle bass note if provided
    if (bass && bass !== fundamental) {
        // Normalize accidentals for comparison (♯ vs #, ♭ vs b)
        const normalizeForComparison = (note) => {
            return note.replace(/♯/g, '#').replace(/♭/g, 'b').toUpperCase();
        };
        const normalizedBass = normalizeForComparison(bass);
        // Remove bass note from chord notes if it exists (avoid duplication)
        const notesWithoutBass = notesInChord.filter(note => {
            const normalizedNote = normalizeForComparison(note);
            return normalizedNote !== normalizedBass;
        });
        // Add bass note at the beginning
        return [bass, ...notesWithoutBass];
    }
    return notesInChord;
};
/**
 * Gets the notes for a slot's chord.
 * Custom chords (slots with customText) return an empty array.
 * @param slot The slot containing the chord
 * @returns Array of note names in the chord, or empty array if slot has customText or no chord
 */
export const getSlotNotes = (slot) => {
    // Custom chords don't have notes
    if (slot.customText) {
        return [];
    }
    // No chord or no root
    if (!slot.chord) {
        return [];
    }
    const v3Chord = normalizeToV3(slot.chord);
    if (!v3Chord.root) {
        return [];
    }
    // Calculate notes using the existing function with the chord object
    return getChordNotes(v3Chord);
};
export const getCompositeQualityString = (chord) => {
    // Check for root (V3) or fundamental (V2/ParsedChordData)
    const hasRoot = chord && (('root' in chord && chord.root) || ('fundamental' in chord && chord.fundamental));
    if (!chord || !hasRoot) {
        return chord?.quality || 'major';
    }
    let qualityForNotes = chord.quality || 'major';
    if (chord.isDominant) {
        if (qualityForNotes === 'major' || qualityForNotes === '' || qualityForNotes === null) {
            if (chord.extension === null || chord.extension === '' || chord.extension === '7') {
                qualityForNotes = 'dominant7';
            }
            else if (chord.extension === '9') {
                qualityForNotes = 'dominant9';
            }
            else if (chord.extension === '11') {
                qualityForNotes = 'dominant11';
            }
            else if (chord.extension === '13') {
                qualityForNotes = 'dominant13';
            }
        }
        else if (qualityForNotes === 'sus2') {
            if (chord.extension === '7' || chord.extension === null || chord.extension === '') {
                qualityForNotes = 'dominant7sus2';
            }
        }
        else if (qualityForNotes === 'sus4') {
            if (chord.extension === '7' || chord.extension === null || chord.extension === '') {
                qualityForNotes = 'dominant7sus4';
            }
            else if (chord.extension === '9') {
                qualityForNotes = 'dominant9sus4';
            }
        }
    }
    else {
        const baseQuality = chord.quality === null || chord.quality === '' ? 'major' : chord.quality;
        qualityForNotes = baseQuality;
        if (baseQuality === 'major') {
            if (chord.extension === '7')
                qualityForNotes = 'major7';
            else if (chord.extension === '6')
                qualityForNotes = 'major6';
            else if (chord.extension === '9')
                qualityForNotes = 'major9';
            else if (chord.extension === '11')
                qualityForNotes = 'major11';
            else if (chord.extension === '13')
                qualityForNotes = 'major13';
        }
        else if (baseQuality === 'minorMajor') {
            if (chord.extension === '7')
                qualityForNotes = 'minorMajor7';
            else if (chord.extension === '9')
                qualityForNotes = 'minorMajor9';
            else if (chord.extension === '11')
                qualityForNotes = 'minorMajor11';
            else if (chord.extension === '13')
                qualityForNotes = 'minorMajor13';
            // minorMajor chords always have an extension, so no need for a base 'minorMajor' case like for major/minor
        }
        else if (baseQuality === 'minor') {
            if (chord.extension === '7')
                qualityForNotes = 'minor7';
            else if (chord.extension === '6')
                qualityForNotes = 'minor6';
            else if (chord.extension === '9')
                qualityForNotes = 'minor9';
            else if (chord.extension === '11')
                qualityForNotes = 'minor11';
            else if (chord.extension === '13')
                qualityForNotes = 'minor13';
        }
        else if (baseQuality === 'diminished') {
            if (chord.extension === '7')
                qualityForNotes = 'diminished7';
            else if (chord.extension === '6')
                qualityForNotes = 'diminished6';
            else if (chord.extension === '9')
                qualityForNotes = 'diminished9';
        }
        else if (baseQuality === 'augmented') {
            if (chord.extension === '7')
                qualityForNotes = 'augmented7';
            else if (chord.extension === '9')
                qualityForNotes = 'augmented9';
        }
        else if (baseQuality === 'power') {
            qualityForNotes = 'power';
        }
    }
    return qualityForNotes;
};
